import React, { useState } from 'react';
import { createCharity } from '../../services/charityService';
import { clearCachedData } from '../../services/cacheService';

const CharityForm = ({ onSuccess, onError }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    website: '',
    stripe_account_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }

    if (formData.logo_url && !isValidUrl(formData.logo_url)) {
      newErrors.logo_url = 'Please enter a valid URL';
    }

    // stripe_account_id is required by the backend (Stripe Connect account
    // the charity receives donations on). Quick shape check for an acct_ id.
    if (!formData.stripe_account_id.trim()) {
      newErrors.stripe_account_id = 'Stripe account ID is required';
    } else if (!/^acct_/.test(formData.stripe_account_id.trim())) {
      newErrors.stripe_account_id = 'Expected a Stripe Connect account ID (acct_...)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const charityData = {
        name: formData.name.trim(),
        description: formData.description,
        website: formData.website,
        logo_url: formData.logo_url,
        stripe_account_id: formData.stripe_account_id.trim(),
      };

      await createCharity(charityData);

      // Clear cache to ensure fresh data on next load
      clearCachedData('charities');

      onSuccess('Charity created and Stripe account verified successfully!');
    } catch (err) {
      console.error('Error creating charity:', err);
      // The backend returns STRIPE_ACCOUNT_INVALID when Stripe rejects the
      // connected account (e.g. charges not enabled, account not found).
      const message = (err.message || '').includes('STRIPE_ACCOUNT_INVALID')
        ? 'Stripe account verification failed. Check that the account exists and has charges enabled.'
        : ('Failed to create charity. ' + (err.message || 'Please try again.'));
      onError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            id="name"
            value={formData.name}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.name ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Enter charity name"
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            name="description"
            id="description"
            rows="3"
            value={formData.description}
            onChange={handleChange}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            placeholder="Enter charity description"
          />
        </div>

        <div>
          <label htmlFor="website" className="block text-sm font-medium text-gray-700">
            Website URL
          </label>
          <input
            type="url"
            name="website"
            id="website"
            value={formData.website}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.website ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="https://example.com"
          />
          {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
        </div>

        <div>
          <label htmlFor="logo_url" className="block text-sm font-medium text-gray-700">
            Logo URL
          </label>
          <input
            type="url"
            name="logo_url"
            id="logo_url"
            value={formData.logo_url}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.logo_url ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="https://example.com/logo.png"
          />
          {errors.logo_url && <p className="mt-1 text-sm text-red-600">{errors.logo_url}</p>}
          {formData.logo_url && (
            <img
              src={formData.logo_url}
              alt="Logo Preview"
              className="mt-2 h-20 w-auto rounded-md object-contain border"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
        </div>

        <div className="md:col-span-2">
          <label htmlFor="stripe_account_id" className="block text-sm font-medium text-gray-700">
            Stripe Connect Account ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="stripe_account_id"
            id="stripe_account_id"
            value={formData.stripe_account_id}
            onChange={handleChange}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
              errors.stripe_account_id ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="acct_xxxxxxxxxxxxxxxx"
          />
          {errors.stripe_account_id && <p className="mt-1 text-sm text-red-600">{errors.stripe_account_id}</p>}
          <p className="mt-1 text-sm text-gray-500">
            The charity's Stripe Connect account that will receive donation transfers.
            On save, the backend verifies the account and the charity becomes
            pledgeable only if Stripe reports charges enabled.
          </p>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={loading}
          className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating & Verifying...' : 'Create Charity'}
        </button>
      </div>
    </form>
  );
};

export default CharityForm;
